import { useCallback, useMemo } from "react";
import {
  useExperiment,
  useGetExperimentPayload,
  useGetExperimentVariant,
} from "@hopper-b2b/experiments";
import { useI18nContext } from "@hopper-b2b/i18n";
import dayjs, { type Dayjs } from "dayjs";
import { FEATURE_FLAGS } from "../../App";

// Variant configuration for Black Friday campaign
const EARN_VARIANTS = {
  "5-percent": { cashbackPercent: 5, pointsPerDollar: 9 },
  "8-percent": { cashbackPercent: 8, pointsPerDollar: 14 },
  "9-percent": { cashbackPercent: 9, pointsPerDollar: 16 },
  "10-percent": { cashbackPercent: 10, pointsPerDollar: 18 },
  "12.5-percent": { cashbackPercent: 12.5, pointsPerDollar: 22 },
} as const;

// Default/fallback values (current 5% campaign)
const DEFAULT_CASHBACK_PERCENT = 5;
const DEFAULT_POINTS_PER_DOLLAR = 9;

export const useEnhancedEarnAmount = () => {
  const { t, formatNumber } = useI18nContext();
  const isEnhancedEarnEnabled = useExperiment(
    FEATURE_FLAGS.ENHANCED_EARN_ENABLED
  );

  const enhancedEarnPointsBackRate = useGetExperimentPayload(
    FEATURE_FLAGS.ENHANCED_EARN_POINTS_BACK_RATE
  )?.pointsBackRates;

  // Get the variant from the EarnPercentage flag
  const earnPercentageVariant = useGetExperimentVariant(
    FEATURE_FLAGS.EARN_PERCENTAGE
  ) as keyof typeof EARN_VARIANTS | null;

  // Determine cashback rate and points per dollar based on variant
  const { cashbackPercent, pointsPerDollar } = useMemo(() => {
    if (earnPercentageVariant && EARN_VARIANTS[earnPercentageVariant]) {
      return EARN_VARIANTS[earnPercentageVariant];
    }
    return {
      cashbackPercent: DEFAULT_CASHBACK_PERCENT,
      pointsPerDollar: DEFAULT_POINTS_PER_DOLLAR,
    };
  }, [earnPercentageVariant]);

  const cashEarnRate = cashbackPercent;

  // Fixed rate from nubank, 1 dollar/BRL = 33 points
  const cashbackToPointsRate = 33;

  const calculateCashEarnAmount = useCallback(
    (totalPrice: number) => {
      return Math.round(totalPrice * cashEarnRate) / 100;
    },
    [cashEarnRate]
  );

  const calculatePointsEarnAmount = useCallback(
    (totalPrice: number) => {
      const cashEarned = calculateCashEarnAmount(totalPrice);
      return Math.round(cashEarned * cashbackToPointsRate);
    },
    [calculateCashEarnAmount]
  );

  const pointsEarnedByDollarCopy = useCallback(() => {
    return t("enhancedEarn.pointsEarnedPerDollar", {
      pointsPerDollar: pointsPerDollar,
    });
  }, [t, pointsPerDollar]);

  const pointsEarnedWithMultiplerCopy = useCallback(
    (totalPrice: number) => {
      const totalPoints = calculatePointsEarnAmount(totalPrice);
      return t("enhancedEarn.pointsEarnedWithPerDollar", {
        pointsPerDollar: pointsPerDollar,
        points: formatNumber(totalPoints, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }),
      });
    },
    [calculatePointsEarnAmount, formatNumber, t, pointsPerDollar]
  );

  const potentialPointsEarnCopy = useCallback(
    (totalPrice: number) => {
      const totalPoints = calculatePointsEarnAmount(totalPrice);
      return t("enhancedEarn.earnPointsWithAmount", {
        points: formatNumber(totalPoints, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }),
      });
    },
    [calculatePointsEarnAmount, formatNumber, t]
  );

  const isBookingAfterLaunch = useCallback(
    (date: Dayjs) => {
      const dateToCompare = dayjs(enhancedEarnPointsBackRate[0].startDate);
      return date.isAfter(dateToCompare);
    },
    [enhancedEarnPointsBackRate]
  );

  return useMemo(() => {
    return {
      isEnhancedEarnEnabled,
      calculatePointsEarnAmount,
      calculateCashEarnAmount,
      pointsEarnedWithMultiplerCopy,
      pointsEarnedByDollarCopy,
      potentialPointsEarnCopy,
      isBookingAfterLaunch,
      cashbackPercent,
      pointsPerDollar,
    };
  }, [
    isEnhancedEarnEnabled,
    calculatePointsEarnAmount,
    calculateCashEarnAmount,
    pointsEarnedWithMultiplerCopy,
    pointsEarnedByDollarCopy,
    potentialPointsEarnCopy,
    isBookingAfterLaunch,
    cashbackPercent,
    pointsPerDollar,
  ]);
};
